import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, finalize, Subject } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { TooltipModule } from 'primeng/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  LucideAngularModule,
  Search,
  ShoppingCart,
  Trash2,
  Plus,
  Minus,
  Camera,
  AlertCircle,
  Package,
  X,
  Tag,
  ChevronUp,
  Printer,
  Share2,
  FileText,
  CheckCircle,
} from 'lucide-angular';

import { ProductService } from '../../core/services/product.service';
import { CategoryService } from '../../core/services/category.service';
import { OrderService } from '../../core/services/order.service';
import { AuthService } from '../../core/services/auth.service';
import { StoreContextService } from '../../core/services/store-context.service';
import { BillingService } from '../../core/services/billing.service';
import { BarcodeScannerComponent } from '../../shared/components/barcode-scanner/barcode-scanner.component';
import {
  VariantPickerComponent,
  SelectedVariant,
} from '../../shared/components/variant-picker/variant-picker.component';
import {
  ProductDetailComponent,
  CartAddEvent,
} from '../../shared/components/product-detail/product-detail.component';
import { ProductDto, CategoryDto } from '../../core/models/product.models';
import { CreateOrderRequest } from '../../core/models/order.models';
import { CustomerDto } from '../../core/models/customer.models';
import {
  ReceiptComponent,
  ReceiptCurrencyTotal,
  ReceiptData,
} from '../../shared/components/receipt/receipt.component';
import { ReceiptPrintService } from '../../shared/services/receipt-print.service';
import { ReceiptExportService } from '../../shared/services/receipt-export.service';
import { CustomerService } from '../../core/services/customer.service';
import { MoneyPipe } from '../../shared/pipes/money.pipe';
import { CurrencyService } from '../../core/services/currency.service';

export interface CartItem {
  productId: string;
  productName: string;
  unitPrice: number;
  quantity: number;
  /** Birlikka to'g'ri keladigan chegirma — qatorning O'Z valyutasida (`currency`). */
  discount: number;
  /** Original % value (0–100) — only set when discount was entered as %. UI only. */
  discountPct?: number;
  stocks: number;
  imageUrl: string | null;
  productVariantId?: string | null;
  variantLabel?: string | null;
  /**
   * Mahsulotning sotuv valyutasi. Savatda har xil valyutali qatorlar bo'lishi
   * MUMKIN — backend savdoni valyuta bo'yicha alohida orderlarga bo'ladi.
   */
  currency: string;
}

type PaymentTypeInt = 0 | 1 | 2;

/** Backenddagi `PaymentMethod` bilan mos: 0=Cash, 1=Card, 2=Bank. */
type PrepaidMethodInt = 0 | 1 | 2;

@Component({
  selector: 'app-sales',
  standalone: true,
  imports: [
    MoneyPipe,
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    DialogModule,
    SelectModule,
    DatePickerModule,
    TooltipModule,
    TranslatePipe,
    LucideAngularModule,
    BarcodeScannerComponent,
    VariantPickerComponent,
    ProductDetailComponent,
    ReceiptComponent,
  ],
  templateUrl: './sales.component.html',
  styleUrls: ['./sales.component.css'],
})
export class SalesComponent implements OnInit {
  private productSvc = inject(ProductService);
  private categorySvc = inject(CategoryService);
  private orderSvc = inject(OrderService);
  private authSvc = inject(AuthService);
  private billingSvc = inject(BillingService);
  private receiptPrinter = inject(ReceiptPrintService);
  private receiptExport = inject(ReceiptExportService);
  private customerSvc = inject(CustomerService);
  readonly currencySvc = inject(CurrencyService);
  storeCtx = inject(StoreContextService);
  private translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  allowProductImages = computed(() => this.billingSvc.hasModule('product_images'));

  // Icons
  Search = Search;
  ShoppingCart = ShoppingCart;
  Trash2 = Trash2;
  Plus = Plus;
  Minus = Minus;
  Camera = Camera;
  AlertCircle = AlertCircle;
  Package = Package;
  X = X;
  Tag = Tag;
  ChevronUp = ChevronUp;
  Printer = Printer;
  Share2 = Share2;
  FileText = FileText;
  CheckCircle = CheckCircle;

  readonly COUNTRY_CODES = [
    { code: '+998', flag: '🇺🇿', label: 'UZ +998' },
    { code: '+7', flag: '🇷🇺', label: 'RU +7' },
    { code: '+7', flag: '🇰🇿', label: 'KZ +7' },
    { code: '+996', flag: '🇰🇬', label: 'KG +996' },
    { code: '+992', flag: '🇹🇯', label: 'TJ +992' },
    { code: '+993', flag: '🇹🇲', label: 'TM +993' },
    { code: '+994', flag: '🇦🇿', label: 'AZ +994' },
    { code: '+374', flag: '🇦🇲', label: 'AM +374' },
    { code: '+380', flag: '🇺🇦', label: 'UA +380' },
    { code: '+375', flag: '🇧🇾', label: 'BY +375' },
    { code: '+1', flag: '🇺🇸', label: 'US +1' },
    { code: '+44', flag: '🇬🇧', label: 'GB +44' },
    { code: '+90', flag: '🇹🇷', label: 'TR +90' },
    { code: '+91', flag: '🇮🇳', label: 'IN +91' },
    { code: '+86', flag: '🇨🇳', label: 'CN +86' },
  ];

  // ── Products ─────────────────────────────────────────────────────────────────
  products = signal<ProductDto[]>([]);
  categories = signal<CategoryDto[]>([]);
  productsLoading = signal(false);
  categoryLoading = signal(false);
  searchText = signal('');
  searchQuery = signal('');

  /**
   * Ekrandagi ro'yxat QAYSI matn natijasi ekani. Qidiruv kechiktirilgani uchun
   * bu `searchQuery` dan orqada qolishi mumkin — `onSearchEnter` shuni tekshiradi.
   */
  private loadedQuery = signal<string | null>(null);

  /**
   * Serverga OXIRGI yuborilgan matn — javob kelishini kutmasdan, so'rov boshlanishida
   * yoziladi. `loadedQuery` bundan farq qiladi: u javob KELGANDAN keyin yangilanadi.
   * Ikkovi kerak, chunki debounce hali javob yo'lda ekan ham ishlab ketishi mumkin.
   */
  private requestedQuery = signal<string | null>(null);
  selectedCategoryId = signal<string | null>(null);

  /** Kategoriya pills uchun statik (client-side) qidiruv + storeType filtri. */
  categorySearch = signal('');
  filteredCategories = computed(() => {
    const q = this.categorySearch().trim().toLowerCase();
    // Tanlangan do'kon turiga (storeType) tegishli kategoriyalar — boshqa turdagilar chiqmaydi.
    const typeId = this.storeCtx.selectedStore()?.storeTypeId ?? null;
    let list = this.categories();
    if (typeId) list = list.filter((c) => c.storeTypeId === typeId);
    if (!q) return list;
    return list.filter((c) => c.name.toLowerCase().includes(q));
  });

  /**
   * Telefonda kategoriya qidiruv inputi ochiqmi. sm dan yuqorida input CSS orqali
   * doim ko'rinadi, shuning uchun bu bayroq faqat kichik ekranga taalluqli.
   */
  categorySearchOpen = signal(false);

  openCategorySearch(el: HTMLInputElement): void {
    this.categorySearchOpen.set(true);
    // Element hali `hidden` — Angular sinfni shu ishlovchi tugagach yangilaydi,
    // `display: none` da esa `focus()` ta'sir qilmaydi. Keyingi kadrga qoldiramiz.
    requestAnimationFrame(() => el.focus());
  }

  /** Bo'sh inputni yopamiz; matn qolgan bo'lsa ochiq turadi — filtr ko'rinmay qolmasin. */
  closeCategorySearchIfEmpty(): void {
    if (!this.categorySearch().trim()) this.categorySearchOpen.set(false);
  }
  private searchSubject = new Subject<string>();
  page = signal(1);
  readonly pageSize = 24;
  totalCount = signal(0);
  hasMore = computed(() => this.page() * this.pageSize < this.totalCount());

  // ── Cart ──────────────────────────────────────────────────────────────────────
  cart = signal<CartItem[]>([]);
  cartSheetOpen = signal(false);
  cartItemCount = computed(() => this.cart().reduce((s, i) => s + i.quantity, 0));
  cartSubtotal = computed(() => this.cart().reduce((s, i) => s + i.unitPrice * i.quantity, 0));
  cartDiscount = computed(() => this.cart().reduce((s, i) => s + i.discount * i.quantity, 0));
  cartTotal = computed(() => this.cartSubtotal() - this.cartDiscount());

  // ══════════════════════════════════════════════════════════════════════════════
  //  Cart item edit overlay (mirrors CartItemEditOverlay.xaml.cs)
  // ══════════════════════════════════════════════════════════════════════════════
  qtyEditItem = signal<CartItem | null>(null);
  /** 'price' = enter total so'm (default), 'qty' = enter quantity */
  editMode = signal<'price' | 'qty'>('price');
  /** 'main' yoki 'discount' — qaysi input faol (ramka rangi shunga qarab) */
  editActiveField = signal<'main' | 'discount'>('main');
  editMainInput = signal('0');
  editDiscountInput = signal('0');
  editDiscountOpen = signal(false);
  editDiscountType = signal<'pct' | 'fixed'>('pct');

  /**
   * Birlik summasini DONA NARXI aniqligigacha yaxlitlaydi.
   *
   * Bu yerda `CurrencyService.round` (roundingStep) ATAYIN ishlatilmaydi: qadam chek
   * yakuniga mo'ljallangan — UZS'da 100, RUB'da 1. Birlik narxga qo'llansa 7 500
   * so'mning 10% i 750 emas, 800 bo'lib ketardi, RUB'da esa 0.65 → 1.
   * `Math.round` ham bo'lmaydi: USD'da 0.653 ning har qanday ulushi 0 yoki 1 ga
   * aylanib, dollarli chegirma umuman ishlamay qolardi.
   *
   * Valyuta kasr xonasi (`decimals`) ham YETARLI EMAS: u summa uchun (USD → 2),
   * dona narxi esa undan aniqroq bo'lishi mumkin (`4.4234 $`). Shuning uchun
   * `roundPrice` — katalog va `OrderItem.UnitPrice` bilan bir xil aniqlik.
   * Aks holda `4.4234 $` lik mahsulotga chegirma qo'yilganda narx `4.42 $` ga
   * "sakrab", kassir kiritmagan raqam chekka tushardi.
   */
  private roundUnit(amount: number, code?: string | null): number {
    return this.currencySvc.roundPrice(amount, code);
  }

  /**
   * Sonni input uchun xom satrga aylantiradi (`1234.5`) — guruh ajratgichsiz,
   * eksponentasiz, ortiqcha nollarsiz. Bu KO'RSATISH emas, shuning uchun `money`
   * pipe bu yerda yaramaydi.
   *
   * ATAYIN yaxlitlanmaydi. Valyuta kasr xonasiga (USD → 2) yaxlitlansa, 0.653 lik
   * mahsulot "0.65" bo'lib ochilar va saqlashda miqdor 0.65/0.653 = 0.9954 ga
   * o'zgarib ketardi — foydalanuvchi hech narsaga tegmasa ham. 12 ta muhim raqam
   * suzuvchi nuqta shovqinini (0.30000000000000004) kesadi, xolos.
   */
  private numberInputText(value: number): string {
    return String(parseFloat(value.toPrecision(12)));
  }

  /** Unit price after discount. Fixed type = total sum entered → divide by qty. */
  editDiscountedUnitPrice = computed(() => {
    const item = this.qtyEditItem();
    if (!item) return 0;
    if (!this.editDiscountOpen()) return item.unitPrice;
    const d = this.editDiscVal();
    const t = this.editDiscountType();
    if (t === 'pct') return this.roundUnit(item.unitPrice * (1 - d / 100), item.currency);
    const qty = this.editMainVal();
    return Math.max(0, item.unitPrice - (qty > 0 ? d / qty : 0));
  });

  editMainVal = computed(() => parseFloat(this.editMainInput()) || 0);
  editDiscVal = computed(() => parseFloat(this.editDiscountInput()) || 0);

  /**
   * Hisoblangan miqdor (ko'rsatish uchun).
   *
   * Aniqlik bazadagi bilan bir xil (`OrderItem.Quantity` = `decimal(18,6)`).
   * Ilgari 4 xona edi va `0.999999` kg `1` ga aylanardi.
   */
  editCalcQty = computed(() => {
    if (this.editMode() === 'qty') return this.editMainVal();
    const price = this.editDiscountedUnitPrice();
    return price > 0 ? this.roundQty(this.editMainVal() / price) : 0;
  });

  /**
   * Miqdorni baza aniqligiga keltiradi — YAXLITLASH emas, suzuvchi nuqta
   * shovqinini kesish. `0.999999` shundayligicha qoladi.
   */
  private roundQty(value: number): number {
    return Number(value.toFixed(SalesComponent.QUANTITY_DECIMALS));
  }

  /** Bazadagi miqdor aniqligi — `OrderItem.Quantity` = `decimal(18,6)`. */
  private static readonly QUANTITY_DECIMALS = 6;

  /** Calculated subtotal for display */
  editCalcSubtotal = computed(() => {
    if (this.editMode() === 'price') return this.editMainVal();
    return this.editDiscountedUnitPrice() * this.editMainVal();
  });

  openQtyEdit(item: CartItem, event?: Event): void {
    event?.stopPropagation();
    this.qtyEditItem.set(item);
    this.editActiveField.set('main');
    if (item.discount > 0) {
      // Chegirma bor — qty mode, oldingi turini (% yoki so'm) tiklash
      this.editMode.set('qty');
      this.editMainInput.set(item.quantity.toString());
      this.editDiscountOpen.set(true);
      if (item.discountPct != null) {
        this.editDiscountType.set('pct');
        this.editDiscountInput.set(item.discountPct.toString());
      } else {
        this.editDiscountType.set('fixed');
        this.editDiscountInput.set(this.numberInputText(item.discount * item.quantity));
      }
    } else {
      // Chegirma yo'q — price mode
      this.editMode.set('price');
      const initTotal = item.unitPrice * item.quantity;
      this.editMainInput.set(initTotal > 0 ? this.numberInputText(initTotal) : '0');
      this.editDiscountOpen.set(false);
      this.editDiscountType.set('fixed');
      this.editDiscountInput.set('0');
    }
  }

  switchEditMode(mode: 'price' | 'qty'): void {
    if (this.editMode() === mode) return;
    this.editMode.set(mode);
    this.editActiveField.set('main');
    const item = this.qtyEditItem();
    if (mode === 'price') {
      const total = item ? (item.unitPrice - item.discount) * item.quantity : 0;
      this.editMainInput.set(total > 0 ? this.numberInputText(total) : '0');
      this.editDiscountOpen.set(false);
      this.editDiscountInput.set('0');
    } else {
      this.editMainInput.set(item && item.quantity > 0 ? item.quantity.toString() : '0');
      if (item && item.discount > 0) {
        this.editDiscountOpen.set(true);
        this.editDiscountType.set('fixed');
        this.editDiscountInput.set(this.numberInputText(item.discount * item.quantity));
      } else {
        this.editDiscountOpen.set(false);
        this.editDiscountInput.set('0');
      }
    }
  }

  toggleEditDiscount(): void {
    const open = !this.editDiscountOpen();
    this.editDiscountOpen.set(open);
    if (!open) {
      this.editDiscountInput.set('0');
      this.editActiveField.set('main');
    } else {
      this.editDiscountType.set('fixed');
      this.editActiveField.set('discount');
    }
  }

  /**
   * Kiritilgan matnni tozalab signalga yozadi.
   *
   * `type="number"` ishlatilmaydi: u brauzerga qarab vergul/nuqtani turlicha qabul
   * qiladi va noto'g'ri kiritishda `value` ni bo'sh satr qilib qaytaradi, ya'ni
   * kassir yozgani sirg'alib ketardi. Shuning uchun matn input + qo'lda tozalash:
   * vergul nuqtaga, harf/emoji (mobil avtotuzatish) tashlab yuboriladi, nuqta bitta.
   */
  onEditInput(event: Event, field: 'main' | 'discount'): void {
    const el = event.target as HTMLInputElement;
    let v = el.value.replace(/,/g, '.').replace(/[^\d.]/g, '');
    const dot = v.indexOf('.');
    if (dot !== -1) v = v.slice(0, dot + 1) + v.slice(dot + 1).replace(/\./g, '');
    if (v === '') v = '0';
    else if (/^0\d/.test(v)) v = v.replace(/^0+/, '');
    // Angular [value] binding qiymat o'zgarmasa DOM'ni yangilamaydi — qo'lda tenglaymiz.
    if (el.value !== v) el.value = v;
    this.editActiveField.set(field);
    if (field === 'main') this.editMainInput.set(v);
    else this.editDiscountInput.set(v);
  }

  /** Fokusda mavjud qiymat tanlanadi — ustiga darhol yozish uchun. */
  onEditFocus(event: FocusEvent, field: 'main' | 'discount'): void {
    this.editActiveField.set(field);
    (event.target as HTMLInputElement).select();
  }

  onEditKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.confirmQtyEdit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.qtyEditItem.set(null);
    }
  }

  confirmQtyEdit(): void {
    const item = this.qtyEditItem();
    if (!item) return;
    const key = this.lineId(item);

    let qty = this.editCalcQty();
    if (qty <= 0) {
      this.removeFromCart(key);
      this.qtyEditItem.set(null);
      return;
    }
    qty = Math.min(item.stocks, this.roundQty(qty));

    // Price mode = entered total IS final, no discount. Qty mode = explicit discount.
    let discFixed = 0;
    if (this.editDiscountOpen() && this.editMode() === 'qty') {
      const d = this.editDiscVal();
      if (this.editDiscountType() === 'pct') {
        const perUnit = this.roundUnit((item.unitPrice * d) / 100, item.currency);
        discFixed = Math.min(item.unitPrice, Math.max(0, perUnit));
      } else {
        // d = total sum discount → per-unit = d / qty. Bu yerda yaxlitlanmaydi:
        // kassir kiritgan UMUMIY chegirma aynan saqlanishi kerak.
        discFixed = qty > 0 ? Math.min(item.unitPrice, Math.max(0, d / qty)) : 0;
      }
    }

    const discPct =
      this.editDiscountOpen() && this.editMode() === 'qty' && this.editDiscountType() === 'pct'
        ? this.editDiscVal()
        : undefined;
    this.cart.update((items) =>
      items.map((i) =>
        this.lineId(i) === key
          ? { ...i, quantity: qty, discount: discFixed, discountPct: discPct }
          : i,
      ),
    );
    this.qtyEditItem.set(null);
  }

  // ── Payment dialog ────────────────────────────────────────────────────────────
  paymentVisible = signal(false);
  paymentType = signal<PaymentTypeInt>(0);
  paidAmount = signal(0);
  /**
   * Nasiyadagi OLDINDAN TO'LOV qaysi yo'l bilan qilingani — naqd/karta/bank.
   * Busiz `buildPayments` uni har doim naqd deb yozardi va kassa/karta/bank
   * qoldig'i haqiqiy pul harakatidan chetga chiqardi.
   */
  prepaidMethod = signal<PrepaidMethodInt>(0);
  debtCustomerName = signal('');
  debtCustomerPhone = signal('');
  matchingCustomers = signal<CustomerDto[]>([]);
  selectedCountryCode = signal('+998');
  /** Nasiya to'lov muddati. null — muddatsiz. */
  debtDueDate = signal<Date | null>(null);
  /** Datepicker minimal sana — bugundan oldin tanlab bo'lmaydi. */
  readonly today = new Date();
  paymentSubmitting = signal(false);

  // ── Valyuta (Bosqich 3) ─────────────────────────────────────────────────────

  /**
   * Savatdagi birinchi valyuta — yorliqlar uchun. Jamlar
   * `cartTotalsByCurrency()` dan olinadi.
   */
  cartCurrency = computed(() => this.cart()[0]?.currency ?? this.currencySvc.defaultCode());

  /**
   * Savat jamlari — HAR valyutada alohida.
   *
   * Aralash savat qo'llab-quvvatlanadi: backend uni valyuta bo'yicha alohida
   * orderlarga bo'ladi, lekin mijoz uchun bu BITTA savdo — bitta chek raqami bilan.
   */
  cartTotalsByCurrency = computed(() => {
    const acc = new Map<string, { subtotal: number; discount: number }>();
    for (const i of this.cart()) {
      const row = acc.get(i.currency) ?? { subtotal: 0, discount: 0 };
      row.subtotal += i.unitPrice * i.quantity;
      row.discount += i.discount * i.quantity;
      acc.set(i.currency, row);
    }
    return [...acc.entries()]
      .map(([currency, v]) => ({
        currency,
        subtotal: v.subtotal,
        discount: v.discount,
        total: v.subtotal - v.discount,
      }))
      .sort((a, b) => b.total - a.total);
  });

  /** Savatda bir nechta valyuta bormi. */
  isMixedCart = computed(() => this.cartTotalsByCurrency().length > 1);

  /** Savat valyutasining belgisi — chegirma/miqdor yorliqlari uchun. */
  cartCurrencySymbol = computed(() => this.currencySvc.info(this.cartCurrency()).symbol);

  /**
   * Ixtiyoriy valyuta kodining belgisi.
   *
   * Aralash savatda qator yorlig'i SAVAT emas, QATOR valyutasini ko'rsatishi kerak —
   * aks holda `$` li mol yonida `so'm` chiqib qoladi.
   */
  symbolOf(code: string): string {
    return this.currencySvc.info(code).symbol;
  }

  // Chek belgilanishi va uning valyuta mantiqi `app-receipt` ichida — orders va
  // debtors ham shu komponentni ishlatadi.

  /** Mijoz QAYSI pulda to'layapti. Savat valyutasidan farq qilishi mumkin (I7). */
  payCurrency = signal('');

  /**
   * Har guruh O'Z valyutasida to'lanadimi.
   *
   * Yoqilganda kurs UMUMAN kerak emas: `$100` dollarda, `300 000` so'mda beriladi.
   * Har order o'z pulida to'liq yopiladi, shuning uchun o'girish xatosi ham,
   * yopilmay qolgan tiyin ham bo'lmaydi.
   */
  payPerCurrency = signal(false);

  /**
   * SHU savdo uchun kelishilgan kurslar (kalit — manba valyutasi).
   *
   * Sozlamadagi kurs faqat BOSHLANG'ICH taklif. Kassir mijoz bilan boshqa kursga
   * kelishishi mumkin (I13) va o'shanda kelishilgan kurs shu savdoga muzlatiladi —
   * sozlamalarga TEGMAYDI, keyingi savdolar avvalgi kursda qoladi.
   */
  private saleRates = signal<Record<string, number>>({});

  /**
   * 1 birlik `from` = shuncha to'lov valyutasi.
   * Kelishilgan kurs bor bo'lsa — o'sha, aks holda sozlamadagi kurs.
   */
  effectiveRate(from: string): number | null {
    const pay = this.payCurrency();
    if (from === pay) return 1;
    const agreed = this.saleRates()[from];
    if (agreed !== undefined && agreed > 0) return agreed;
    return this.currencySvc.rate(from, pay);
  }

  /** Sozlamadagi kurs — "asliga qaytarish" tugmasi va taqqoslash uchun. */
  settingsRate(from: string): number | null {
    return from === this.payCurrency() ? 1 : this.currencySvc.rate(from, this.payCurrency());
  }

  /** Kassir shu savdo uchun kursni o'zgartirganmi. */
  isRateAgreed(from: string): boolean {
    return this.saleRates()[from] !== undefined;
  }

  setSaleRate(from: string, value: number): void {
    this.saleRates.update((m) => ({ ...m, [from]: value }));
    this.refreshSuggestedAmounts();
  }

  /** Kelishilgan kursni bekor qilib, sozlamadagisiga qaytaradi. */
  clearSaleRate(from: string): void {
    this.saleRates.update((m) => {
      const next = { ...m };
      delete next[from];
      return next;
    });
    this.refreshSuggestedAmounts();
  }

  /** 1 birlik savat valyutasi = shuncha to'lov valyutasi. Topilmasa `null`. */
  payRate = computed(() => this.effectiveRate(this.cartCurrency()));

  /** Kurs kerak bo'lgan biror guruh uchun kurs topilmadimi. */
  private missingRate = computed(() => this.cartTotalInPayCurrency() === null);

  /**
   * To'lash kerak bo'lgan jami — TO'LOV valyutasida.
   * Har valyuta guruhi o'z kursi bilan o'giriladi. Biror kurs yo'q bo'lsa `null`.
   *
   * `CurrencyService.round` (roundingStep) ISHLATILMAYDI: u summani kassa qadamiga
   * tortadi va USD'da `0.999999` lik savdo `1` ga aylanardi — savdo hujjatida
   * mijoz to'lamagan summa yozilardi. Qadam faqat TAKLIF ko'rsatishda o'rinli
   * (`suggestedAmount`), qarz hisobida emas.
   */
  cartTotalInPayCurrency = computed(() => {
    const pay = this.payCurrency();
    let sum = 0;
    for (const g of this.cartTotalsByCurrency()) {
      const rate = this.effectiveRate(g.currency);
      if (rate === null) return null;
      sum += g.total * rate;
    }
    return this.currencySvc.roundPrice(sum, pay);
  });

  /**
   * Boshqa valyutada to'lanayaptimi — shunda ekranda kurs va ekvivalent ko'rsatiladi.
   * Aralash savatda kamida bitta guruh to'lov valyutasidan farq qilsa ham shunday.
   *
   * Har guruh o'z pulida to'lanayotgan bo'lsa — o'girish yo'q, demak `false`.
   */
  isCrossCurrency = computed(
    () =>
      !this.payPerCurrency() &&
      this.cartTotalsByCurrency().some((g) => g.currency !== this.payCurrency()),
  );

  /** Kurs qatori ko'rsatiladigan guruhlar — to'lov valyutasidan farq qiladiganlari. */
  rateRows = computed(() =>
    this.payPerCurrency()
      ? []
      : this.cartTotalsByCurrency().filter((g) => g.currency !== this.payCurrency()),
  );

  /**
   * Guruh jamisi — to'lov valyutasida. Kurs yo'q bo'lsa `null`.
   *
   * `round` EMAS: bu ekranda "shuncha to'lash kerak" deb ko'rsatiladi va kassir
   * shu raqamni kiritadi. Qadamga tortilsa `0.999999` lik guruh `1` bo'lib
   * ko'rinardi va qarz tiyinigacha yopilmasdi.
   */
  convertedTotal(g: { currency: string; total: number }): number | null {
    const rate = this.effectiveRate(g.currency);
    return rate === null ? null : this.currencySvc.roundPrice(g.total * rate, this.payCurrency());
  }

  /** To'lov rejimi almashganda taklif summalari qayta hisoblanadi. */
  setPayPerCurrency(value: boolean): void {
    this.payPerCurrency.set(value);
    this.perCurrencyPaid.set({});
    this.refreshSuggestedAmounts();
  }

  /** Kurs kerak, lekin kiritilmagan — savdoni o'tkazib bo'lmaydi. */
  rateMissing = computed(() => this.isCrossCurrency() && this.missingRate());

  /**
   * Kursi topilmagan BIRINCHI valyuta — xato xabarida shu nomlanadi.
   * Aralash savatda `cartCurrency()` boshqa guruhni ko'rsatib qo'yishi mumkin edi.
   */
  missingRateFrom = computed(
    () =>
      this.cartTotalsByCurrency().find((g) => this.effectiveRate(g.currency) === null)?.currency ??
      this.cartCurrency(),
  );

  /** To'lash kerak bo'lgan jami, to'lov valyutasida. Kurs yo'q bo'lsa savat jamisi. */
  dueInPayCurrency = computed(() => this.cartTotalInPayCurrency() ?? this.cartTotal());

  /**
   * "O'z pulida" rejimida har valyuta uchun alohida berilgan summa.
   * Kalit — valyuta kodi. Bo'sh bo'lsa guruh jamisi olinadi.
   */
  private perCurrencyPaid = signal<Record<string, number>>({});

  /**
   * Shu guruh uchun mijoz bergan summa.
   *
   * Sukut qiymati TO'LOV TURIGA bog'liq: naqd/kartada to'liq summa taklif
   * qilinadi, NASIYADA esa 0 — oldindan to'lov ixtiyoriy. Aks holda nasiya
   * savdosi jimgina to'liq to'langan bo'lib qolardi va qarz yo'qolardi.
   */
  paidFor(currency: string): number {
    const explicit = this.perCurrencyPaid()[currency];
    if (explicit !== undefined) return explicit;
    if (this.paymentType() === 2) return 0;
    return this.cartTotalsByCurrency().find((g) => g.currency === currency)?.total ?? 0;
  }

  /** Shu guruh uchun qolib ketadigan qarz (nasiyada). */
  debtFor(currency: string): number {
    const total = this.cartTotalsByCurrency().find((g) => g.currency === currency)?.total ?? 0;
    return Math.max(0, total - this.paidFor(currency));
  }

  setPaidFor(currency: string, value: number): void {
    this.perCurrencyPaid.update((m) => ({ ...m, [currency]: value }));
  }

  /** Shu guruh bo'yicha qaytim — o'z valyutasida. */
  changeFor(currency: string): number {
    // Nasiyada qaytim yo'q: ortiqcha berilgani qarzni yopadi, qaytarilmaydi.
    if (this.paymentType() === 2) return 0;
    const total = this.cartTotalsByCurrency().find((g) => g.currency === currency)?.total ?? 0;
    return Math.max(0, this.paidFor(currency) - total);
  }

  change = computed(() => {
    if (this.paymentType() !== 0) return 0;
    const due = this.cartTotalInPayCurrency();
    if (due === null) return 0;
    return Math.max(0, this.paidAmount() - due);
  });

  paymentTypeOptions: { label: string; value: PaymentTypeInt }[] = [
    { label: 'SALES.PAYMENT.CASH', value: 0 },
    { label: 'SALES.PAYMENT.CARD', value: 1 },
    { label: 'SALES.PAYMENT.DEBT', value: 2 },
  ];

  prepaidMethodOptions: { label: string; value: PrepaidMethodInt }[] = [
    { label: 'SALES.PAYMENT.CASH', value: 0 },
    { label: 'SALES.PAYMENT.CARD', value: 1 },
    { label: 'FINANCE.METHOD.Bank', value: 2 },
  ];

  /** Nasiyada oldindan biror summa kiritilganmi — shundagina to'lov turi so'raladi. */
  hasAnyPrepaid = computed(() =>
    this.payPerCurrency()
      ? this.cartTotalsByCurrency().some((g) => this.paidFor(g.currency) > 0)
      : this.paidAmount() > 0,
  );

  isPaymentValid = computed(() => {
    if (!this.cart().length || !this.storeCtx.selectedStoreId()) return false;
    // Kurs yo'q bo'lsa savdo o'tmaydi — taxmin qilinmaydi.
    if (this.rateMissing()) return false;
    const t = this.paymentType();
    if (t === 1) return true;
    if (t === 2) return !!this.debtCustomerName().trim() && !!this.debtCustomerPhone().trim();
    // Naqd: har guruh o'z pulida to'lansa — har biri alohida to'liq bo'lishi kerak.
    return this.payPerCurrency()
      ? this.cartTotalsByCurrency().every((g) => this.paidFor(g.currency) >= g.total)
      : this.paidAmount() >= this.dueInPayCurrency();
  });

  /** Date → 'YYYY-MM-DD' (local, timezone siljishisiz). null bo'lsa null. */
  private formatDueDate(d: Date | null): string | null {
    if (!d) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  // ── Receipt dialog ────────────────────────────────────────────────────────────
  receiptVisible = signal(false);
  receipt = signal<ReceiptData | null>(null);
  /**
   * Saqlangan orderning id'si — Excel/ulashish uchun. Aralash savatda backend bir
   * necha order yaratadi, lekin javobda BIRINCHISI qaytadi va u chek raqami orqali
   * qolganlariga bog'langan, shuning uchun bitta id yetarli.
   */
  receiptOrderId = signal<string | null>(null);
  exportBusy = signal(false);
  shareBusy = signal(false);

  // ── Product detail dialog ─────────────────────────────────────────────────────
  detailProduct = signal<ProductDto | null>(null);
  detailVisible = signal(false);

  // ── Init ──────────────────────────────────────────────────────────────────────
  ngOnInit(): void {
    // Do'kon filtri uchun ro'yxat (header global yuklamagan bo'lsa).
    if (this.storeCtx.stores().length === 0) {
      this.storeCtx.loadStores();
    }

    this.categorySvc
      .getAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (res) => this.categories.set(res.data ?? []) });

    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        // Skaner barkodni ~80 ms da yozib, DARHOL Enter yuboradi — ya'ni Enter
        // debounce'dan oldin ishlab, so'rovni allaqachon jo'natgan bo'ladi. Bu
        // tekshiruvsiz 300 ms keyin aynan o'sha matn bilan ikkinchi bir xil
        // so'rov ketardi.
        if (this.requestedQuery() === value.trim()) return;
        this.loadProducts(true);
      });

    this.loadProducts(true);
  }

  // ── Product loading ───────────────────────────────────────────────────────────
  /**
   * @param onLoaded natijalar kelgach chaqiriladi — Enter bosilganda ro'yxat
   *   yangilanishini kutish uchun (pastdagi `onSearchEnter` ga qarang).
   */
  loadProducts(reset = false, onLoaded?: () => void): void {
    if (reset) this.page.set(1);
    this.productsLoading.set(true);
    const storeId = this.storeCtx.selectedStoreId();
    const storeIds = storeId ? [storeId] : undefined;
    const query = this.searchQuery().trim();
    this.requestedQuery.set(query);

    this.productSvc
      .getProducts(
        this.page(),
        this.pageSize,
        this.searchQuery() || undefined,
        storeIds,
        this.selectedCategoryId(),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const items = res.data?.items ?? [];
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          reset ? this.products.set(items) : this.products.update((p) => [...p, ...items]);
          this.totalCount.set(res.data?.totalCount ?? 0);
          this.loadedQuery.set(query);
          onLoaded?.();
        },
        error: () => {
          this.productsLoading.set(false);
          this.categoryLoading.set(false);
        },
        complete: () => {
          this.productsLoading.set(false);
          this.categoryLoading.set(false);
        },
      });
  }

  onSearch(value: string): void {
    this.searchText.set(value);
    this.searchQuery.set(value);
    this.searchSubject.next(value);
  }

  /**
   * Enter bosilganda birinchi topilgan mahsulotni savatga qo'shadi.
   *
   * <p>
   * Qidiruv 300 ms kechikish bilan ishlaydi, simli skaner esa barkodni yozib
   * DARHOL Enter yuboradi — o'sha paytda ro'yxatda hali oldingi natijalar
   * turadi. Shuning uchun ro'yxat aynan shu matn natijasi ekaniga ishonch hosil
   * qilinadi; aks holda skaner boshqa mahsulotni qo'shib yuborardi yoki mavjud
   * tovarni "topilmadi" deb ko'rsatardi.
   * </p>
   */
  onSearchEnter(): void {
    const query = this.searchText().trim();
    if (!query) {
      // Bo'sh input bilan Enter — filtrni tozalash. `searchQuery` ham bo'shatilmasa,
      // oldingi Enterdan qolgan so'rov ro'yxatni filtrlangan holda ushlab turardi:
      // maydon bo'sh, lekin grid hamon o'sha barkod natijasini ko'rsatardi.
      if (!this.searchQuery()) return;
      this.searchQuery.set('');
      this.loadedQuery.set(null);
      this.loadProducts(true);
      return;
    }

    if (this.loadedQuery() === query) {
      this.addFirstMatch(query);
      return;
    }
    this.loadProducts(true, () => this.addFirstMatch(query));
  }

  private addFirstMatch(query: string): void {
    // faqat input bo'shaydi — ro'yxat filtri joyida qoladi
    this.searchText.set('');

    const product = this.products()[0];
    if (!product) {
      this.productSvc.showWarn(this.translate.instant('SALES.SEARCH_NOT_FOUND', { query }));
      return;
    }
    this.addToCart(product);
  }

  selectCategory(id: string | null): void {
    this.categoryLoading.set(true);
    this.selectedCategoryId.set(id);
    this.loadProducts(true);
  }

  /** Do'kon filtri o'zgarganda — kontekstni yangilab, mahsulotlarni qayta yuklaymiz. */
  onStoreChange(storeId: string | null): void {
    this.storeCtx.setSelectedStoreId(storeId);
    this.loadProducts(true);
  }

  loadMore(): void {
    this.page.update((p) => p + 1);
    this.loadProducts(false);
  }

  // ── Product detail / quick add ─────────────────────────────────────────────────
  /**
   * Mahsulot bosilganda: variantli bo'lsa detail oynasini ochamiz (u yerda variant
   * tanlanadi), aks holda to'g'ridan-to'g'ri savatga qo'shamiz — ortiqcha oyna yo'q.
   */
  openDetail(product: ProductDto): void {
    if (product.hasVariants) {
      this.detailProduct.set(product);
      this.detailVisible.set(true);
    } else {
      this.addToCart(product);
    }
  }

  onDetailAddToCart(event: CartAddEvent): void {
    const key = this.lineId(event);
    this.cart.update((items) => {
      const existing = items.find((i) => this.lineId(i) === key);
      if (existing) {
        const newQty = Math.min(existing.quantity + event.quantity, event.stocks);
        return items.map((i) =>
          this.lineId(i) === key
            ? {
                ...i,
                quantity: newQty,
                variantLabel: event.variantLabel,
                unitPrice: event.unitPrice,
                stocks: event.stocks,
              }
            : i,
        );
      }
      return [
        ...items,
        {
          productId: event.productId,
          productName: event.productName,
          unitPrice: event.unitPrice,
          quantity: event.quantity,
          discount: this.roundUnit((event.unitPrice * event.discount) / 100, event.currency),
          discountPct: event.discount > 0 ? event.discount : undefined,
          stocks: event.stocks,
          imageUrl: event.imageUrl,
          productVariantId: event.productVariantId,
          variantLabel: event.variantLabel,
          currency: event.currency,
        },
      ];
    });
    this.detailVisible.set(false);
  }

  // ── Barcode ───────────────────────────────────────────────────────────────────
  onBarcodeDetected(barcode: string): void {
    // Skaner o'zini to'xtatib, overlay'ni yopadi — bu yerda yopish kerak emas.
    const storeId = this.storeCtx.selectedStoreId();
    if (!storeId) {
      this.productSvc.showWarn(this.translate.instant('SALES.SELECT_STORE_FIRST'));
      return;
    }

    this.productSvc
      .getByBarcodeForSale(barcode, storeId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          // Javobsiz qaytish kassir uchun eng yomon holat edi: skaner "biq" etadi,
          // savat esa o'zgarmaydi va sabab noma'lum qoladi.
          if (!result) {
            this.productSvc.showWarn(
              this.translate.instant('SALES.BARCODE_NOT_FOUND', { barcode }),
            );
            return;
          }
          this.addToCartFromBarcode(result);
        },
        // Toast'ni servis chiqaradi (404 shu yergacha yetib kelmaydi) — bu yerda
        // faqat oqimni yutamiz, aks holda konsolda ushlanmagan xato qoladi.
        error: () => undefined,
      });
  }

  private addToCartFromBarcode(
    result: import('../../core/models/product.models').WeightCodeResult,
  ): void {
    const key = this.lineId({ productId: result.productId, productVariantId: null });
    const existing = this.cart().find((i) => this.lineId(i) === key);

    if (result.isWeight && result.weightKg != null) {
      // Tarozi mahsuloti: miqdor = og'irlik (kg), narx = unitPrice * kg
      const qty = result.weightKg;
      if (existing) {
        this.cart.update((items) =>
          items.map((i) =>
            this.lineId(i) === key
              ? { ...i, quantity: i.quantity + qty, unitPrice: result.sellPrice }
              : i,
          ),
        );
      } else {
        this.cart.update((items) => [
          ...items,
          {
            productId: result.productId,
            productName: result.name,
            unitPrice: result.sellPrice,
            quantity: qty,
            discount: this.roundUnit(
              (result.sellPrice * (result.discount ?? 0)) / 100,
              result.sellCurrency,
            ),
            stocks: result.currentStock,
            imageUrl: null,
            productVariantId: null,
            currency: result.sellCurrency,
          },
        ]);
      }
      return;
    }

    // Oddiy mahsulot
    if (result.currentStock <= 0) {
      this.productSvc.showWarn(this.translate.instant('SALES.STOCK_EMPTY', { name: result.name }));
      return;
    }
    if (existing) {
      if (existing.quantity >= result.currentStock) {
        this.warnStockLimit(result.name, result.currentStock);
        return;
      }
      this.cart.update((items) =>
        items.map((i) => (this.lineId(i) === key ? { ...i, quantity: i.quantity + 1 } : i)),
      );
    } else {
      this.cart.update((items) => [
        ...items,
        {
          productId: result.productId,
          productName: result.name,
          unitPrice: result.sellPrice,
          quantity: 1,
          discount: result.discount ?? 0,
          stocks: result.currentStock,
          imageUrl: null,
          productVariantId: null,
          currency: result.sellCurrency,
        },
      ]);
    }
  }

  // ── Cart operations ───────────────────────────────────────────────────────────

  /**
   * Savat qatori identifikatori: mahsulot + variant. Bir mahsulotning turli
   * variantlari (masalan Qizil-M va Ko'k-L) alohida qatorlar bo'lishi uchun.
   */
  lineId(item: { productId: string; productVariantId?: string | null }): string {
    return `${item.productId}::${item.productVariantId ?? ''}`;
  }

  /**
   * Qoldiq tugagani yoki savatda allaqachon butun qoldiq borligi haqidagi xabar.
   *
   * <p>
   * Ilgari bu ikkala holat ham jimgina `return` edi: kassir mahsulotni bosar,
   * savat esa qimirlamasdi. Sabab ekranda ko'rinmagani uchun bu "dastur qotib
   * qoldi" degan tushunchaga olib kelardi.
   * </p>
   */
  private warnStockLimit(name: string, stocks: number): void {
    // Qoldiq — pul emas, shuning uchun valyuta formatlagichi ishlatilmaydi: u
    // UZS uchun kasrni yo'qotib, 2.5 kg ni "3" qilib ko'rsatardi.
    this.productSvc.showWarn(this.translate.instant('SALES.STOCK_LIMIT', { name, count: stocks }));
  }

  addToCart(product: ProductDto): void {
    if (product.stocks <= 0) {
      this.productSvc.showWarn(this.translate.instant('SALES.STOCK_EMPTY', { name: product.name }));
      return;
    }
    const key = this.lineId({ productId: product.id, productVariantId: null });
    const existing = this.cart().find((i) => this.lineId(i) === key);
    if (existing) {
      if (existing.quantity >= product.stocks) {
        this.warnStockLimit(product.name, product.stocks);
        return;
      }
      this.cart.update((items) =>
        items.map((i) => (this.lineId(i) === key ? { ...i, quantity: i.quantity + 1 } : i)),
      );
    } else {
      this.cart.update((items) => [
        ...items,
        {
          productId: product.id,
          productName: product.name,
          unitPrice: product.sellPrice,
          quantity: 1,
          discount: this.roundUnit(
            (product.sellPrice * (product.discount ?? 0)) / 100,
            product.sellCurrency,
          ),
          discountPct: (product.discount ?? 0) > 0 ? (product.discount ?? 0) : undefined,
          stocks: product.stocks,
          imageUrl: product.imageUrl,
          productVariantId: null,
          currency: product.sellCurrency,
        },
      ]);
    }
  }

  // ── Variant picker ────────────────────────────────────────────────────────────
  pickerVisible = signal(false);
  pickerProduct = signal<ProductDto | null>(null);

  openVariantPicker(product: ProductDto): void {
    this.pickerProduct.set(product);
    this.pickerVisible.set(true);
  }

  onVariantSelected(sel: SelectedVariant): void {
    const product = this.pickerProduct();
    const key = this.lineId({ productId: sel.productId, productVariantId: sel.variantId });
    this.cart.update((items) => {
      const existing = items.find((i) => this.lineId(i) === key);
      if (existing) {
        // Aynan shu variant qatori bor — miqdorni oshiramiz (qoldiqdan oshmasin)
        return items.map((i) =>
          this.lineId(i) === key ? { ...i, quantity: Math.min(i.quantity + 1, sel.stock) || 1 } : i,
        );
      }
      return [
        ...items,
        {
          productId: sel.productId,
          productName: product?.name ?? sel.label,
          unitPrice: sel.price,
          quantity: 1,
          discount: this.roundUnit(
            (sel.price * (product?.discount ?? 0)) / 100,
            product?.sellCurrency,
          ),
          discountPct: (product?.discount ?? 0) > 0 ? (product?.discount ?? 0) : undefined,
          stocks: sel.stock,
          imageUrl: product?.imageUrl ?? null,
          productVariantId: sel.variantId,
          variantLabel: sel.label,
          currency: product?.sellCurrency ?? this.currencySvc.defaultCode(),
        },
      ];
    });
  }

  increaseQty(key: string): void {
    const item = this.cart().find((i) => this.lineId(i) === key);
    if (!item || item.quantity >= item.stocks) return;
    this.cart.update((items) =>
      items.map((i) => (this.lineId(i) === key ? { ...i, quantity: i.quantity + 1 } : i)),
    );
  }

  decreaseQty(key: string): void {
    const item = this.cart().find((i) => this.lineId(i) === key);
    if (!item) return;
    if (item.quantity <= 1) this.removeFromCart(key);
    else
      this.cart.update((items) =>
        items.map((i) => (this.lineId(i) === key ? { ...i, quantity: i.quantity - 1 } : i)),
      );
  }

  removeFromCart(key: string): void {
    this.cart.update((items) => items.filter((i) => this.lineId(i) !== key));
  }

  clearCart(): void {
    this.cart.set([]);
    this.cartSheetOpen.set(false);
  }

  itemTotal(item: Pick<CartItem, 'unitPrice' | 'discount' | 'quantity'>): number {
    return (item.unitPrice - item.discount) * item.quantity;
  }

  // ── Payment ───────────────────────────────────────────────────────────────────
  openPayment(): void {
    if (!this.cart().length) return;
    this.paymentType.set(0);
    // Har savdo toza boshlanadi: oldingi savdoda kelishilgan kurs bu yerga o'tmaydi.
    this.saleRates.set({});
    this.perCurrencyPaid.set({});
    this.payPerCurrency.set(false);
    // Default — savat valyutasida to'lash. Kassir boshqasini tanlashi mumkin.
    this.payCurrency.set(this.cartCurrency());
    this.paidAmount.set(this.suggestedAmount());
    this.prepaidMethod.set(0);
    this.debtCustomerName.set('');
    this.debtCustomerPhone.set('');
    this.selectedCountryCode.set('+998');
    this.debtDueDate.set(null);
    this.cartSheetOpen.set(false);
    this.paymentVisible.set(true);
  }

  /** To'lov turi almashganda paidAmount'ni mosla: nasiyada 0 (oldindan to'lov yo'q),
   *  naqd/kartada to'liq summa. */
  onPaymentTypeChange(type: PaymentTypeInt): void {
    this.paymentType.set(type);
    this.paidAmount.set(type === 2 ? 0 : this.suggestedAmount());
    // Qo'lda kiritilgan guruh summalari eski turga tegishli edi — tozalanadi,
    // shunda `paidFor` yangi tur uchun to'g'ri sukut qiymatini beradi.
    this.perCurrencyPaid.set({});
    this.prepaidMethod.set(0);
  }

  /**
   * To'lov valyutasi almashganda taklif qilinadigan summa qayta hisoblanadi.
   * Bu faqat TAKLIF — yakuniy summani kassir belgilaydi (I13).
   */
  onPayCurrencyChange(code: string): void {
    this.payCurrency.set(code);
    // Kelishilgan kurs ESKI to'lov valyutasiga tegishli edi — u endi ma'nosiz.
    this.saleRates.set({});
    this.refreshSuggestedAmounts();
  }

  /** Taklif qilinadigan summalarni joriy kurs/rejimga moslaydi. */
  private refreshSuggestedAmounts(): void {
    if (this.paymentType() !== 2) this.paidAmount.set(this.suggestedAmount());
  }

  /**
   * To'lovni valyuta guruhlariga taqsimlaydi.
   *
   * Mijoz bitta summa beradi (masalan hammasini so'mda), lekin savat bir necha
   * valyutali bo'lishi mumkin. Har guruhga o'z ulushi biriktiriladi; oxirgi
   * guruhga QOLDIQ beriladi, shunda yaxlitlash tufayli tiyin yo'qolmaydi.
   */
  private buildPayments(paid: number, type: PaymentTypeInt) {
    const pay = this.payCurrency();
    // Naqd/karta to'liq to'lovda tur o'zi usulni belgilaydi. Nasiyada oldindan
    // to'lov kassir tanlagan usulda yoziladi — aks holda hammasi "naqd" bo'lib
    // yozilib, karta/bank qoldig'i haqiqiy pul harakatidan chetga chiqib qolardi.
    const method = type === 1 ? 1 : type === 2 ? this.prepaidMethod() : 0;
    const groups = this.cartTotalsByCurrency();

    const result: {
      amount: number;
      method: number;
      currency: string;
      rate: number | null;
      forCurrency: string;
    }[] = [];

    // Har guruh O'Z pulida: o'girish yo'q, kurs 1, qarz tiyinigacha yopiladi.
    if (this.payPerCurrency()) {
      for (const g of groups) {
        // Nasiyada ham AYNI qoida: mijoz nima bergan bo'lsa o'sha yoziladi.
        // Avval bu yerda `type === 2 ? g.total` turgan edi va nasiya savdosi
        // to'liq to'langan bo'lib chiqib, qarz umuman yaratilmasdi.
        const amount = Math.min(this.paidFor(g.currency), g.total);
        if (amount > 0) {
          result.push({
            amount,
            method,
            currency: g.currency,
            rate: 1,
            forCurrency: g.currency,
          });
        }
      }
      return result;
    }

    let remaining = paid;
    groups.forEach((g, index) => {
      const rate = this.effectiveRate(g.currency);
      const isLast = index === groups.length - 1;
      // Oxirgi guruh qoldiqni oladi — yaxlitlash farqi yo'qolmasin.
      // `round` EMAS: bu mijoz to'lagan haqiqiy summa, kassa qadamiga tortilsa
      // qarz hisobida tiyinlik farq qolardi.
      const amount = isLast
        ? remaining
        : Math.min(remaining, this.currencySvc.roundPrice((rate ?? 1) * g.total, pay));
      remaining -= amount;
      if (amount > 0) {
        result.push({ amount, method, currency: pay, rate, forCurrency: g.currency });
      }
    });

    return result;
  }

  /**
   * Ekranda ko'rsatiladigan taklif — to'lov valyutasida.
   *
   * `Math.ceil` OLIB TASHLANDI: u `0.999999` lik savdoni `1` deb taklif qilardi
   * va kassir tasdiqlasa, mijoz to'lamagan summa yozilardi. Taklif ham aniq
   * bo'lishi kerak — kassir xohlasa uni qo'lda yaxlitlaydi.
   */
  private suggestedAmount(): number {
    const due = this.cartTotalInPayCurrency();
    return due ?? this.currencySvc.roundPrice(this.cartTotal(), this.cartCurrency());
  }

  onCustomerFieldChange(term: string): void {
    if (term.trim().length >= 2) {
      this.customerSvc.searchCustomers(term.trim()).subscribe({
        next: (res) => {
          this.matchingCustomers.set(res.data ?? []);
        },
        error: () => this.matchingCustomers.set([]),
      });
    } else {
      this.matchingCustomers.set([]);
    }
  }

  selectCustomer(cust: CustomerDto): void {
    this.debtCustomerName.set(cust.fullName);

    let phone = cust.phoneNumber;
    let matchedCode = '+998';
    for (const c of this.COUNTRY_CODES) {
      if (phone.startsWith(c.code)) {
        matchedCode = c.code;
        phone = phone.substring(c.code.length);
        break;
      }
    }
    this.selectedCountryCode.set(matchedCode);
    this.debtCustomerPhone.set(phone);
    this.matchingCustomers.set([]);
  }

  confirmPayment(): void {
    if (!this.isPaymentValid()) return;
    const user = this.authSvc.user();
    if (!user) return;

    const storeId = this.storeCtx.selectedStoreId()!;
    const type = this.paymentType();
    // "O'z pulida" rejimida yagona summa yo'q — har guruh alohida to'lanadi va
    // `buildPayments` o'zi taqsimlaydi. Bu yerdagi son faqat eski maydon uchun.
    const paid = this.payPerCurrency()
      ? this.cartTotalsByCurrency().reduce(
          (s, g) => s + Math.min(this.paidFor(g.currency), g.total),
          0,
        )
      : type === 1
        ? this.dueInPayCurrency()
        : this.paidAmount();
    // Business owner (Customer) is not in Employee table → null
    const cashierId = user.role === 'Customer' ? null : user.id;

    // Generate receipt number: same algo as beposApp
    // Guid.NewGuid().ToString("N").ToUpper().Substring(0, 10)
    const localOrderId = crypto.randomUUID();
    const receiptNumber = localOrderId.replace(/-/g, '').slice(0, 10).toUpperCase();

    const cartSnapshot = [...this.cart()];
    const subtotal = this.cartSubtotal();
    const discount = this.cartDiscount();
    const total = this.cartTotal();
    // Chek uchun jamlar SAVAT tozalanishidan oldin olinadi va valyuta bo'yicha
    // ajratiladi — aralash savatda yagona jami ma'nosiz (`$100 + 300 000`).
    const perCurrency = this.payPerCurrency();
    const receiptTotals: ReceiptCurrencyTotal[] = this.cartTotalsByCurrency().map((g) => ({
      ...g,
      // O'z pulida to'langanda o'girish yo'q — kurs 1, to'lov guruh ichida.
      rate: perCurrency ? 1 : (this.effectiveRate(g.currency) ?? 1),
      ...(perCurrency && type === 0
        ? { paid: this.paidFor(g.currency), change: this.changeFor(g.currency) }
        : {}),
    }));
    const receiptCurrency = this.cartCurrency();
    // O'z pulida to'langanda yagona to'lov valyutasi yo'q — kurs qatori bosilmasin.
    const receiptPayCurrency = perCurrency ? receiptCurrency : this.payCurrency();
    const receiptPayRate = perCurrency ? 1 : (this.payRate() ?? 1);
    // To'lash kerak bo'lgan jami — to'lov valyutasida. Qaytim SHUNDAN hisoblanadi.
    const dueInPayCurrency = this.cartTotalInPayCurrency() ?? total;

    const request: CreateOrderRequest = {
      storeId,
      cashierId,
      paymentType: type,
      paidAmount: paid,
      localOrderId,
      receiptNumber,
      items: cartSnapshot.map((i) => ({
        productId: i.productId,
        productVariantId: i.productVariantId ?? null,
        quantity: i.quantity,
        discount: i.discount,
        // Savatdagi narx — chekda chop etiladigan va mijoz to'laydigan narx.
        // Backend katalogdan qayta hisoblamasin.
        unitPrice: i.unitPrice,
      })),
      // Ism har turda yuboriladi — naqd/kartada IXTIYORIY. Backend uni nasiyada
      // majburiy qiladi va faqat o'shanda `Customer` yozuvini yaratadi; naqdda ism
      // shunchaki orderda saqlanadi, ya'ni har o'tkinchi xaridor mijozlar
      // ma'lumotnomasiga tushib ketmaydi.
      debtCustomerName: this.debtCustomerName().trim() || null,
      // Telefon faqat nasiyada so'raladi — mijozni aynan u aniqlaydi.
      debtCustomerPhone:
        type === 2 ? this.selectedCountryCode() + this.debtCustomerPhone().trim() : null,
      debtDueDate: type === 2 ? this.formatDueDate(this.debtDueDate()) : null,
      // Kassaga tushgan REAL pul — mijoz bergan valyutada. Aralash savatda savdo
      // valyuta bo'yicha bir necha orderga bo'linadi, shuning uchun har to'lov
      // `forCurrency` bilan o'z orderiga biriktiriladi (I7).
      payments: paid > 0 ? this.buildPayments(paid, type) : null,
    };

    this.paymentSubmitting.set(true);
    this.orderSvc
      .create(request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.paymentSubmitting.set(false);
          if (res.success) {
            this.paymentVisible.set(false);
            this.cart.set([]);
            this.loadProducts(true);
            const orderId = res.data?.orderId ?? null;
            this.receiptOrderId.set(orderId);
            // PDF darhol olinadi: "Ulashish" bosilganda `navigator.share()` bosish
            // aktivatsiyasi ichida sinxron chaqirilishi kerak (servisga qarang).
            if (orderId) this.receiptExport.prefetch(orderId);
            this.receipt.set({
              receiptNumber,
              storeName: this.storeCtx.selectedStore()?.name ?? '',
              cashierName: user.fullName ?? '',
              date: new Date(),
              items: cartSnapshot,
              subtotal,
              discount,
              total,
              paymentType: type,
              paidAmount: paid,
              // Qaytim TO'LOV valyutasida (I12) — mijoz nima bergan bo'lsa o'shanda.
              change: type === 0 ? Math.max(0, paid - dueInPayCurrency) : 0,
              currency: receiptCurrency,
              totalsByCurrency: receiptTotals,
              payPerCurrency: perCurrency,
              payCurrency: receiptPayCurrency,
              payRate: receiptPayRate,
              debtCustomerName: this.debtCustomerName().trim() || null,
            });
            this.receiptVisible.set(true);
          } else {
            this.orderSvc.showError(
              res.errors?.[0] ?? this.translate.instant('SALES.PAYMENT.ERROR'),
            );
          }
        },
        error: (err) => {
          const msg =
            this.orderSvc['extractError'](err) || this.translate.instant('SALES.PAYMENT.ERROR');
          this.orderSvc.showError(msg);
          this.paymentSubmitting.set(false);
        },
      });
  }

  // ── Receipt ───────────────────────────────────────────────────────────────────

  printReceipt(): void {
    this.receiptPrinter.print();
  }

  /**
   * PDF faqat SAQLANGAN savdo uchun yasaladi — fayl backenddan olinadi. Order
   * yaratilmagan bo'lsa (yoki javobda id kelmasa) tugmalar o'chirilgan turadi.
   */
  downloadReceiptPdf(): void {
    const orderId = this.receiptOrderId();
    if (!orderId || this.exportBusy()) return;

    this.exportBusy.set(true);
    this.receiptExport
      .downloadPdf(orderId)
      .pipe(finalize(() => this.exportBusy.set(false)))
      .subscribe();
  }

  shareReceipt(): void {
    const orderId = this.receiptOrderId();
    if (!orderId || this.shareBusy()) return;

    this.shareBusy.set(true);
    this.receiptExport
      .sharePdf(orderId)
      .pipe(finalize(() => this.shareBusy.set(false)))
      .subscribe();
  }
  onlyNumbers(event: KeyboardEvent) {
    const charCode = event.which || event.keyCode;

    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }
}
